/**
 * MapInitialization Module
 * 
 * Handles map setup, tile layers, basic configuration, and location markers.
 * This module provides the core map initialization functionality.
 */

import Logger from '../../lib/logger';

export class MapInitialization {
  constructor(controller) {
    this.controller = controller;
  }

  // Initialize the map with basic configuration
  initMap() {
    // Check if map already exists and clean it up first
    if (this.controller.map) {
      // Remove existing scale control if it exists
      if (this.controller.scaleControl && this.controller.map.hasControl && this.controller.map.hasControl(this.controller.scaleControl)) {
        this.controller.map.removeControl(this.controller.scaleControl);
        this.controller.scaleControl = null;
      }
      // Remove existing zoom control if it exists
      if (this.controller.zoomControl && this.controller.map.hasControl && this.controller.map.hasControl(this.controller.zoomControl)) {
        this.controller.map.removeControl(this.controller.zoomControl);
        this.controller.zoomControl = null;
      }
      // Remove the map instance
      this.controller.map.remove();
      this.controller.map = null;
    }

    // Use instance methods for configurable values (Phase 1 refactoring)
    const DEFAULT_CENTER = this.controller.DEFAULT_CENTER;
    const DEFAULT_ZOOM = this.controller.DEFAULT_ZOOM;
    const tileConfig = this.controller.getTileConfig();
    const tileURL = tileConfig.URL;
    const attribution = tileConfig.ATTRIBUTION;

    // Initialize the map with zoom control disabled (we'll add it on the right)
    this.controller.map = L.map(this.controller.mapdivTarget, {
      zoomControl: false
    }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);

    // Add zoom control on the right side (only if not already added)
    if (!this.controller.zoomControl) {
      this.controller.zoomControl = L.control.zoom({
        position: 'topright'
      }).addTo(this.controller.map);
    }

    // Add scale bar control (only if not already added)
    // First, remove any existing scale controls from the map container
    const mapContainer = this.controller.mapdivTarget;
    const existingScaleControls = mapContainer.querySelectorAll('.leaflet-control-scale');
    existingScaleControls.forEach(control => {
      control.remove();
    });

    if (!this.controller.scaleControl) {
      this.controller.scaleControl = L.control.scale({
        position: 'bottomleft',
        metric: true,
        imperial: false,
        maxWidth: 150
      }).addTo(this.controller.map);
    }

    // Add zoom level indicator
    this.addZoomLevelIndicator();

    // Add tile layer
    const tiles = L.tileLayer(tileURL, { attribution });
    tiles.addTo(this.controller.map);

    // Add marker if coordinates are provided
    this.addLocationMarker();

    // Initialize map components
    this.initializeMapComponents();
  }

  // Add zoom level indicator control
  addZoomLevelIndicator() {
    const zoomLevelControl = L.control({ position: 'bottomleft' });

    zoomLevelControl.onAdd = (map) => {
      // Create div with CSS class (styles defined in _map_viewer.scss)
      // CSP-compliant: no inline styles
      const div = L.DomUtil.create('div', 'leaflet-control-zoom-level');
      div.textContent = `Zoom: ${map.getZoom()}`;

      // Update zoom level on zoom events
      map.on('zoomend', () => {
        div.textContent = `Zoom: ${map.getZoom()}`;
      });

      return div;
    };

    zoomLevelControl.addTo(this.controller.map);
    this.controller.zoomLevelControl = zoomLevelControl;
  }

  // Add location marker if coordinates are available
  addLocationMarker() {
    const lat = this.controller.latValue;
    const long = this.controller.longValue;

    if (!lat || !long) return;

    // Use instance methods for configurable values (Phase 1 refactoring)
    const MARKER_ZOOM = this.controller.MARKER_ZOOM;
    const icons = this.controller.getIcons();
    const markerIcon = this.createIcon(icons.MARKER, {
      anchor: this.controller.constructor.CONSTANTS.ICON_ANCHOR,
      popupAnchor: this.controller.constructor.CONSTANTS.MARKER_POPUP_ANCHOR
    });

    const marker = L.marker([parseFloat(lat), parseFloat(long)], { icon: markerIcon });
    marker.addTo(this.controller.map);

    // Set view to marker location
    this.controller.map.setView([lat, long], MARKER_ZOOM);
  }

  // Create standardized icon
  createIcon(iconUrl, options = {}) {
    const { ICON_SIZE, ICON_ANCHOR, POPUP_ANCHOR } = this.controller.constructor.CONSTANTS;

    return L.icon({
      iconUrl,
      iconSize: ICON_SIZE,
      iconAnchor: options.anchor || ICON_ANCHOR,
      popupAnchor: options.popupAnchor || POPUP_ANCHOR
    });
  }

  // Initialize map components (info control, legend, layers)
  initializeMapComponents() {
    this.initializeInfoControl();
    this.initializeLegend();
    this.initializeLayers();
    this.setupTopoJSONExtension();
  }

  // Initialize info control
  initializeInfoControl() {
    this.controller.info = L.control({ position: 'topleft' });

    this.controller.info.onAdd = function (map) {
      this._div = L.DomUtil.create('div', 'info card');
      this.update();
      return this._div;
    };

    // Captured here (not inside .update below, where `this` is the Leaflet control, not this
    // module) — props.NOME_MUNIC comes from TopoJSON feature data; escaping it before building
    // innerHTML closes a latent XSS if that data source is ever attacker-influenced.
    const escape = this.controller.escapeHtml.bind(this.controller);

    // Method to update the control based on feature properties
    this.controller.info.update = function (props) {
      const title = '<h4>&nbsp; Ethernet SLA &nbsp;</h4>';
      const content = props
        ? `<b>&nbsp;${escape(props.NOME_MUNIC)}</b><br />`
        : '&nbsp; selecione uma cidade';
      this._div.innerHTML = title + content;
    };
  }

  // Initialize legend control
  initializeLegend() {
    this.controller.legend = L.control({ position: 'bottomright' });

    // CSP-compliant: using CSS classes instead of inline styles
    const colorClasses = ['legend-color-green', 'legend-color-blue', 'legend-color-red'];
    const grades = ['99.9%', '99.5%', '99.0%'];

    this.controller.legend.onAdd = function (map) {
      const div = L.DomUtil.create('div', 'info legend card');

      // Generate legend items using CSS classes (CSP-compliant)
      for (let i = 0; i < grades.length; i++) {
        div.innerHTML += `
          <span>&nbsp;
            <i class="${colorClasses[i]}"></i>&nbsp;
            ${grades[i]}&nbsp;
          </span>
        `;
      }

      return div;
    };
  }

  // Initialize layers
  initializeLayers() {
    this.controller.bids = L.layerGroup();
  }

  // Setup TopoJSON extension for Leaflet
  setupTopoJSONExtension() {
    // Extend Leaflet to create a GeoJSON layer from a TopoJSON file
    L.TopoJSON = L.GeoJSON.extend({
      addData: function (data) {
        if (data.type === "Topology") {
          for (const key in data.objects) {
            if (data.objects.hasOwnProperty(key)) {
              const geojson = topojson.feature(data, data.objects[key]);
              L.GeoJSON.prototype.addData.call(this, geojson);
            }
          }
          return this;
        }
        L.GeoJSON.prototype.addData.call(this, data);
        return this;
      }
    });

    L.topoJson = function (data, options) {
      return new L.TopoJSON(data, options);
    };

    // Initialize GeoJSON layer with improved configuration
    this.initializeGeoJSONLayer();
  }

  // Initialize GeoJSON layer with proper event handlers
  initializeGeoJSONLayer() {
    const self = this.controller;

    this.controller.geojson = L.topoJson(null, {
      style: (feature) => this.getFeatureStyle(feature),
      onEachFeature: (feature, layer) => this.setupFeatureEvents(feature, layer),
      filter: () => true // Can be customized later
    });
  }

  // Get style for GeoJSON features
  getFeatureStyle(feature) {
    const fillColor = feature.properties.cor || '#cccccc'; // Default gray if no color

    // Debug styling for first few features
    if (this.controller && this.controller.styleDebugCount !== undefined && this.controller.styleDebugCount < 5) {
      Logger.debug('Feature styling:', {
        codigo: feature.properties.CODIGO,
        nome: feature.properties.NOME_MUNIC,
        cor: feature.properties.cor,
        fillColor,
        hasCorProperty: 'cor' in feature.properties
      });
      this.controller.styleDebugCount++;
    }

    return {
      color: 'white',
      opacity: 0.3,
      weight: 0,
      fillColor,
      fillOpacity: 0.6
    };
  }

  // Setup events for each feature
  setupFeatureEvents(feature, layer) {
    layer.on({
      mouseover: (e) => this.highlightFeature(e),
      mouseout: (e) => this.resetHighlight(e),
      click: (e) => this.zoomToFeature(e)
    });
  }

  // Highlight feature on mouseover
  highlightFeature(e) {
    const layer = e.target;
    this.controller.info.update(layer.feature.properties);

    layer.setStyle({
      weight: 5,
      color: '#666',
      dashArray: '',
      fillOpacity: 0.8
    });

    // Bring to front if browser supports it, but ensure controls stay on top
    if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
      // Use requestAnimationFrame to ensure controls are re-established after layer change
      requestAnimationFrame(() => {
        layer.bringToFront();
        // Ensure controls maintain their z-index after layer operation
        this.ensureControlsOnTop();
      });
    }
  }

  // Ensure map controls stay on top after layer operations
  // Note: z-index is now handled by CSS rule in _map_viewer.scss
  // .leaflet-control-container .leaflet-control { z-index: 1000 !important; }
  ensureControlsOnTop() {
    // CSS handles z-index, no JavaScript needed
    // This method is kept for backward compatibility
    return;
  }

  // Reset highlight on mouseout
  resetHighlight(e) {
    this.controller.info.update();
    this.controller.geojson.resetStyle(e.target);
  }

  // Zoom to feature on click
  zoomToFeature(e) {
    this.controller.map.fitBounds(e.target.getBounds());
  }

  // Center map on location with circle toggle
  center(event) {
    event.preventDefault();
    event.stopPropagation();

    const lat = this.controller.latValue;
    const long = this.controller.longValue;
    // Use instance methods for configurable values (Phase 1 refactoring)
    const CENTER_ZOOM = this.controller.CENTER_ZOOM;
    const CIRCLE_RADIUS = this.controller.CIRCLE_RADIUS;

    if (lat && long) {
      // Toggle circle behavior
      if (this.controller.circle && this.controller.map.hasLayer(this.controller.circle)) {
        // Circle exists and is on map - remove it (toggle off)
        Logger.debug('Toggling off radius circle');
        this.controller.map.removeLayer(this.controller.circle);
        // Don't center the map when removing the circle
      } else {
        // Circle doesn't exist or not on map - create/add it (toggle on)
        Logger.debug('Toggling on radius circle');

        // Center the map only when adding the circle
        this.controller.map.setView([lat, long], CENTER_ZOOM);

        if (!this.controller.circle) {
          // Create circle with lower z-index to appear behind popups
          this.controller.circle = L.circle([parseFloat(lat), parseFloat(long)], {
            color: 'blue',
            fillColor: '#f03',
            fillOpacity: 0.3,
            radius: CIRCLE_RADIUS,
            interactive: false, // Make it non-interactive so it doesn't interfere with popups
            pane: 'overlayPane' // Use overlay pane which is behind popups
          });

          this.controller.circle.bindPopup(`${CIRCLE_RADIUS / 1000}km radius`);
        }

        this.controller.circle.addTo(this.controller.map);

        // Ensure circle appears behind other layers by bringing other layers to front
        this.ensureProperLayerOrdering();
      }
    } else if (this.controller.mapklayer && this.controller.map.hasLayer(this.controller.mapklayer)) {
      // No coordinates provided - fit bounds to KML layer
      this.controller.map.fitBounds(this.controller.mapklayer.getBounds());
    }
  }

  // Ensure proper layer ordering (circle behind, markers/popups in front)
  ensureProperLayerOrdering() {
    // For layer groups, we need to iterate through each layer and bring them to front
    if (this.controller.bids && this.controller.map.hasLayer(this.controller.bids)) {
      this.controller.bids.eachLayer(layer => {
        if (layer.bringToFront) {
          layer.bringToFront();
        }
      });
    }

    if (this.controller.geojson && this.controller.map.hasLayer(this.controller.geojson)) {
      if (this.controller.geojson.bringToFront) {
        this.controller.geojson.bringToFront();
      }
    }

    if (this.controller.mapklayer && this.controller.map.hasLayer(this.controller.mapklayer)) {
      this.controller.mapklayer.eachLayer(layer => {
        if (layer.bringToFront) {
          layer.bringToFront();
        }
      });
    }
  }

  // Clear map layers and controls
  clear(event) {
    event.preventDefault();
    event.stopPropagation();

    try {
      // Remove controls
      if (this.controller.info && this.controller.map.hasControl && this.controller.map.hasControl(this.controller.info)) {
        this.controller.map.removeControl(this.controller.info);
      }

      if (this.controller.legend && this.controller.map.hasControl && this.controller.map.hasControl(this.controller.legend)) {
        this.controller.map.removeControl(this.controller.legend);
      }

      // Remove layers
      if (this.controller.geojson && this.controller.map.hasLayer(this.controller.geojson)) {
        this.controller.map.removeLayer(this.controller.geojson);
      }

      if (this.controller.mapklayer && this.controller.map.hasLayer(this.controller.mapklayer)) {
        this.controller.mapklayer.clearLayers();
      }

      Logger.debug('Map cleared successfully');
    } catch (error) {
      Logger.error('Error clearing map:', error);
    }
  }
}
